<template>
  <BasePage :pageTitle="__DICT__.menuOptions.Transfers" show-menu show-footer>
    <div v-for="transferId in transferIds" :key="transferId" class="card mb-2">
      <router-link :to="`/transfer/${transferId}`">
        <div class="card-header">
          <div class="card-status-top bg-primary"></div>
          <h4 class="card-title">{{ __DICT__.transfer }} {{ transferId }}</h4>
          <div class="card-actions">
            <span class="badge bg-primary-lt border-primary" v-if="isInbound(transferId)">
              {{ __DICT__.inbound }}
            </span>
            <span class="badge bg-primary text-white" v-if="isOutbound(transferId)">
              {{ __DICT__.outbound }}
            </span>
          </div>
        </div>
        <div class="card-body">
          <div class="text-muted">
            {{ formatFirstDeparture(getTransferTransportInfo(transferId)) }}
          </div>
        </div>
      </router-link>
    </div>

    <div v-if="isLoading" class="text-center mt-3">
      <div class="spinner-border text-primary" role="status"></div>
      <span class="ms-2">{{ __DICT__.loading }}</span>
    </div>
  </BasePage>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useWeekStore } from '@/stores/week.js'
import BasePage from "@/components/BasePage.vue"
import {formatDateWithTimezone} from "@/services/time.js";

const weekStore = useWeekStore()
const weekData = ref([])
const isLoading = ref(true)

const transferPassengers = computed(() => {
  // Helper function to find transfer transport by type
  const findTransfer = (passenger, type) => {
    return passenger.allocPlaces?.find(place => {
      const transport = place.transport
      return transport?.transportType === "transfer" && transport?.type === type
    })?.transport
  }

  // Helper function to add passenger to transfer group
  const updateTransferGroup = (transfers, transport, passenger, isInbound) => {
    if (!transport?.coach) return

    if (!transfers[transport.coach]) {
      transfers[transport.coach] = {
        transportInboundInfo: null,
        transportOutboundInfo: null,
        inboundPassengers: [],
        outboundPassengers: []
      }
    }

    const group = transfers[transport.coach]

    if (isInbound) {
      group.transportInboundInfo = transport
      if (!group.inboundPassengers.includes(passenger)) {
        group.inboundPassengers.push(passenger)
      }
    } else {
      group.transportOutboundInfo = transport
      if (!group.outboundPassengers.includes(passenger)) {
        group.outboundPassengers.push(passenger)
      }
    }
  }

  // Main processing logic
  if (!weekData.value) return {}
  return weekData.value.reduce((transfers, passenger) => {
    const transportInbound = findTransfer(passenger, "transport-inbound")
    const transportOutbound = findTransfer(passenger, "transport-outbound")

    if (transportInbound) {
      updateTransferGroup(transfers, transportInbound, passenger, true)
    }
    if (transportOutbound) {
      updateTransferGroup(transfers, transportOutbound, passenger, false)
    }

    return transfers
  }, {})
})

const transferIds = computed(() => {
  return Object.keys(transferPassengers.value)
      .sort((a, b) => {
        const numA = parseInt(a.replace(/[^0-9]/g, ''))
        const numB = parseInt(b.replace(/[^0-9]/g, ''))
        return numA - numB
      })
})

function getPassengersForTransfer(transferId, direction) {
  if(direction === 'inbound') {
    return transferPassengers.value[transferId]?.inboundPassengers || []
  }
  return transferPassengers.value[transferId]?.outboundPassengers || []
}

function isInbound(transferId) {
  return transferPassengers.value[transferId].transportInboundInfo
}

function isOutbound(transferId) {
  return transferPassengers.value[transferId].transportOutboundInfo
}

function getTransferTransportInfo(transferId) {
  return [transferPassengers.value[transferId]?.transportInboundInfo, transferPassengers.value[transferId]?.transportOutboundInfo]
}

function formatFirstDeparture(transportInfo) {
  let res = ''
  if(transportInfo[1]) {
    const firstDeparture = transportInfo[1].departures.sort((a, b) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
    )[0]
    res += `${firstDeparture.location} - ${firstDeparture.date_time_formatted} ${firstDeparture.date_formatted.replace(/-/g,'/')}`
  }

  if(transportInfo[0]) {
    const firstDeparture = transportInfo[0].departures.sort((a, b) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
    )[0]
    if (res.length > 0) res += `\n`;
    res += `${firstDeparture.location} - ${firstDeparture.date_time_formatted} ${firstDeparture.date_formatted.replace(/-/g,'/')}`
  }

  return res;

}

onMounted(async () => {
  try {
    weekData.value = await weekStore.weekData
  } catch (error) {
    console.error('Error loading transfer data:', error)
  } finally {
    isLoading.value = false
  }
})
</script>